import {
  scalarButton,
  scalarCallout,
  scalarFlex,
  scalarMath,
  scalarEmbed,
  scalarCodeBlock,
  scalarImage,
  scalarTabs,
  scalarTab,
} from '@scalar-org/guide-elements/elements'
import { customHeadingStatic } from '@scalar-org/guide/editor/extensions/RichTextExtensions/CustomHeading'
import { customPageLinkStatic } from '@scalar-org/guide/editor/extensions/RichTextExtensions/CustomPageLink'
import { customPageTitleStatic } from '@scalar-org/guide/editor/extensions/RichTextExtensions/CustomPageTitle'
import { customTabsStatic } from '@scalar-org/guide/editor/extensions/RichTextExtensions/CustomTabs'

/**
 * Mapping of custom component placeholder tags to their respective render functions
 *
 * Previously we use the tiptap `name` as the element substitution tag.
 * Going forward we will use the `tagName` as the element substitution tag.
 * Any tiptap extensions MUST be updated to render their HTML using the `tagName`
 *
 * These render functions will convert any elements that use their tagName into
 * static HTML strings.
 *
 * There may be elements here that are are not in the `customElements` as these
 * render functions may replace the rendering of conventional tags (like `code`)
 *
 * RUN NEAR THE END OF THE PIPELINE AFTER ANY CUSTOM ELEMENTS HAVE BEEN PARSED
 * This functions handles the actual rendering after any custom attribute parsing by `customElements`
 */
export const customComponentRender: Record<
  string,
  (props: any) => Promise<string>
> = {
  [scalarButton.tagName]: scalarButton.render,
  [scalarFlex.tagName]: scalarFlex.render,
  [scalarMath.tagName]: scalarMath.render,
  [scalarCallout.tagName]: scalarCallout.render,
  [scalarEmbed.tagName]: scalarEmbed.render,
  [scalarCodeBlock.tagName]: scalarCodeBlock.render,
  [customHeadingStatic.name]: customHeadingStatic.render,
  [scalarImage.tagName]: scalarImage.render,
  [customPageTitleStatic.name]: customPageTitleStatic.render,
  [customTabsStatic.name]: customTabsStatic.render,
  [customPageLinkStatic.name]: customPageLinkStatic.render,
  [scalarTabs.tagName]: scalarTabs.render,
  [scalarTab.tagName]: scalarTab.render,
} as const

/** List of all placeholder tag names for substitution */
export const componentNames = Object.keys(customComponentRender)

// -----------------------------

// Markdown elements

type ScalarElement = {
  name: string
  tagName: string
  attributeTransform: (
    content: string,
    attributes: Record<string, string | number | undefined>,
  ) => any
}
export type ScalarElementTag =
  | 'flex'
  | 'scalar-button'
  | 'scalar-callout'
  | 'scalar-embed'
  | 'scalar-image'
  | 'scalar-math'
  | 'scalar-code'
  | 'scalar-tabs'
  | 'scalar-tab'

/**
 * Custom directives that do not have a 1:1 association in markdown
 *
 * These are different from the "standard" scalar rendered components
 * such as tables, headings, and code blocks which share the ScalarXZY
 * render component with the WYSIWYG editor but do not require special
 * markdown syntax to render.
 *
 * Custom elements will include a specific tagName.
 * The tag name can be in two ways for custom elements:
 *
 * a markdown directive format:
 * ex. `::scalar-button[My Button]{href=https://google.com icon=solid/mail-send-envelope }`
 * The `content` property is inside of the square brackets while additional attributes
 * are whitespace delimited pairs inside of the braces.
 *
 * or a custom html tag like:
 * ex. `<scalar-button href="https://google.com" icon="solid/mail-send-envelope">My Button</scalar-button>`
 * ex. `<scalar-button data-href="https://google.com" data-icon="solid/mail-send-envelope">My Button</scalar-button>`
 *
 * Custom attributes that do not conflict will be parsed into component props. The `data-` prefix
 * is also supported where attribute names may be at risk of a conflict.
 *
 * In both cases additional nested content can be provided inside. In the render function this
 * content will be replace the `ssg-children` placeholder tag during rendering.
 *
 * THESE RUN EARLY TO PARSE CUSTOM TAGS + DIRECTIVES FOR LATER RENDERING VIA `customComponentRender`
 */
export const customElements: Record<ScalarElementTag, ScalarElement> = {
  [scalarButton.name]: scalarButton,
  [scalarCallout.name]: scalarCallout,
  [scalarEmbed.name]: scalarEmbed,
  [scalarImage.name]: scalarImage,
  [scalarMath.name]: scalarMath,
  [scalarFlex.name]: scalarFlex,
  [scalarCodeBlock.name]: scalarCodeBlock,
  [scalarTabs.name]: scalarTabs,
  [scalarTab.name]: scalarTab,
}

export const scalarElementTags = Object.keys(
  customElements,
) as ScalarElementTag[]
